import * as React from "react";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { Folder } from "lucide-react";
import { api } from "$convex/convexApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type CreateFolderDialogProps = {
  authUserId: string;
  parentPath?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (folderPath: string) => void;
};

export function CreateFolderDialog({
  authUserId,
  parentPath,
  open,
  onOpenChange,
  onSuccess,
}: CreateFolderDialogProps) {
  const [name, setName] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const createFolder = useMutation(api.knowledgeBase.createFolder);

  const handleCreate = React.useCallback(async () => {
    if (!name.trim()) {
      setError("Folder name is required");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const result = await createFolder({
        authUserId,
        name: name.trim(),
        parentPath: parentPath || undefined,
      });

      toast.success(result.message);
      onOpenChange(false);
      setName("");
      onSuccess?.(result.folderPath);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create folder";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  }, [authUserId, createFolder, name, onOpenChange, onSuccess, parentPath]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !isCreating) {
        event.preventDefault();
        handleCreate();
      }
    },
    [handleCreate, isCreating],
  );

  React.useEffect(() => {
    if (!open) {
      setName("");
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder className="size-5" />
            Create New Folder
          </DialogTitle>
          <DialogDescription>
            {parentPath
              ? `Create a new folder inside "${parentPath}"`
              : "Create a new top-level folder"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              placeholder="e.g., Operations, SOPs, Training"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isCreating}
              autoFocus
            />
            {error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Use letters, numbers, spaces, hyphens, and underscores only. No slashes allowed.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating || !name.trim()}>
            {isCreating ? "Creating..." : "Create Folder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
